// Which agent a pairing is with. A pairing is one route on one computer,
// and a route is a harness there (plugins/changeling/scripts/store.mjs,
// `routeId`: the first 32 hex characters of sha256("agent\0" + harness)).
// Every harness on a computer shares its fingerprint, so the route is what
// tells Claude Code and Codex there apart before the agent is paired. A
// link's route is the agent's word, not a proof: the fingerprint is what
// identifies the agent, and the route only what it says it is.

/** The harnesses the changeling plugin names its routes after. */
export type Harness = 'claude' | 'codex' | 'agent';
const HARNESSES: readonly Harness[] = ['claude', 'codex', 'agent'];

/** Each harness as the person knows it. */
export const HARNESS_TEXT: Readonly<Record<Harness, string>> = {
  claude: 'Claude Code',
  codex: 'Codex',
  agent: 'Another agent (not Claude Code or Codex)',
};
/** A route that is no harness's: not one the plugin makes. */
export const UNKNOWN_HARNESS = 'Unknown: not Claude Code, Codex or another agent with the changeling plugin';
/** What a link's route says it is: a harness, none the plugin makes (`other`), or, without a digest here, can't be told (`untold`). */
export type RouteHarness = Harness | 'other' | 'untold';

/** A harness as a pairing records it or a route says it, for the person. */
export function harnessText(harness: string | null): string {
  if (harness === 'untold') return 'Can’t tell: this browser can’t check the link’s route';
  return harness !== null && Object.hasOwn(HARNESS_TEXT, harness) ? HARNESS_TEXT[harness as Harness] : UNKNOWN_HARNESS;
}

const hex = (bytes: ArrayBuffer) => [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');
let routes: Promise<Map<string, Harness> | null> | undefined;
/** The routes of the harnesses; null where the page has no digest (an insecure context). */
function harnessRoutes(): Promise<Map<string, Harness> | null> {
  return Promise.all(HARNESSES.map(async (h) => [hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`agent\0${h}`))).slice(0, 32), h] as const))
    .then((pairs) => new Map(pairs), () => null);
}
/** What `route` says its agent is. */
export async function routeHarness(route: string): Promise<RouteHarness> {
  routes ??= harnessRoutes();
  const known = await routes;
  return known ? (known.get(route.toLowerCase()) ?? 'other') : 'untold';
}
